'use client';

import type { FC } from 'react';
import { formatApiError, apiErrorMessage } from '../../utils/apiError';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Confetti from 'react-confetti-boom';
import { ScreenContent, ScreenFixedBottom, ScreenFixedTop } from '../../screen';
import { Button, ButtonGroup, Text } from '../../components/ui';
import { ContractSuccessResult } from './ContractSuccessResult';
import { DesktopResultLayout } from '../../components/DesktopResultLayout';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { ContractFAQSheet } from './ContractFAQSheet';
import { ContractHistorySheet } from './ContractHistorySheet';
import { ContractInventoryList } from './ContractInventoryList';
import { ContractInventorySheet } from './ContractInventorySheet';
import { Crate } from './Crate';
import { PrizeRangeBar } from './PrizeRangeBar';
import { useCrateRoll } from './useCrateRoll';
import { Credits } from '../../components/Credits.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../contexts/ApiContext.tsx';
import { queryKeys } from '../../lib/queryClient';
import { playEarnings } from '../../lib/uiSounds';
import { loadSounds } from '../../lib/audioEngine';
import { useHaptic } from '../../hooks/useHaptic.ts';
import { useGameMotionEnabled } from '../../hooks/useMotion';
import { useLocalTranslation } from '../../hooks/useLocalTranslation.ts';
import { pluralize } from '../../i18n/pluralize';
import { useConfirmPopup } from '../../hooks/useConfirmPopup';
import { useNavigation } from '../../contexts/NavigationContext';
import { MIN_UPGRADE_ITEM_PRICE } from '../../queries/useUpgradeQueries';
import { extractSkinName, formatCreditsNumber } from '../../utils/formatters.tsx';
import { cn } from '../../utils/cn';
import type { ScreenParams } from '../../types/navigation.ts';
import type { ContractResult, UserInventoryItemResponse } from '../../services/api.ts';
import type { ContractItem, ContractPreviewData } from '../../types/contracts.ts';
import { CONTRACT_LIMITS, validateContractSelection } from '../../types/contracts.ts';
import translations from './ContractScreen.translations.ts';
import './ContractScreen.css';
import { FaShare, FaPlus, FaClock, FaQuestionCircle } from '../../components/icons';
import { useShareWin } from '../../hooks/useShareWin';
import { isBigWin } from '../../utils/bigWin';

interface ContractScreenProps {
  params?: ScreenParams;
}

type ContractError =
  | { kind: 'preview'; text: string }
  | { kind: 'sell'; text: string }
  | { kind: 'execute'; text: string };

// WHY: превью летит на каждый тап по предмету, а теперь ещё и считает ожидание по каталогу
const PREVIEW_DEBOUNCE_MS = 300;

export const ContractScreen: FC<ContractScreenProps> = () => {
  const { t, lang } = useLocalTranslation(translations);
  const isDesktop = useIsDesktop();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { impact, notification } = useHaptic();
  const navigation = useNavigation();
  const gameMotionEnabled = useGameMotionEnabled();

  const [selectedItems, setSelectedItems] = useState<ContractItem[]>([]);
  const [preview, setPreview] = useState<ContractPreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<ContractError | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [contractResult, setContractResult] = useState<ContractResult | null>(null);
  useEffect(() => { void loadSounds(['item_drop', 'jackpot', 'no_profit']).catch(() => undefined); }, []);
  const [faqOpen, setFaqOpen] = useState(false);
  const previewRequestRef = useRef(0);
  const pageScrollRef = useRef<HTMLDivElement | null>(null);

  const roll = useCrateRoll(gameMotionEnabled);
  const { reset: resetRoll } = roll;

  const contractShareName = contractResult?.outputItem.name ?? '';
  const contractSharePrice = contractResult?.outputItem.value ?? 0;
  const contractSourcePrice = contractResult?.totalInputValue ?? 0;
  const contractIsBigWin = isBigWin(contractSharePrice, contractSourcePrice);
  const contractPriceStr = formatCreditsNumber(contractSharePrice);
  const contractInterp = { itemName: contractShareName, price: contractPriceStr };

  const { share: handleShareContract, isSharing: isShareContractLoading, isBigWin: isContractBigWin } = useShareWin({
    inventoryItemId: contractResult?.outputItem.id ?? '',
    itemName: contractShareName,
    itemPrice: contractSharePrice,
    itemHashName: contractResult?.outputItem.hashName ?? contractShareName,
    sourcePrice: contractSourcePrice,
    caption: contractIsBigWin ? t('shareCaptionBigWin', contractInterp) : t('shareCaption', contractInterp),
    buttonText: t('shareButton'),
    fallbackText: t('shareFallback', contractInterp),
  });

  const resetContract = useCallback(() => {
    setShowResult(false);
    setContractResult(null);
    setSelectedItems([]);
    setError(null);
    resetRoll();
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
  }, [queryClient, resetRoll]);

  const showPopup = useConfirmPopup();

  const handleUpgradeResult = () => {
    if (contractResult) {
      navigation.navigate('upgrade', { itemId: contractResult.outputItem.id });
    }
  };

  const handleSellItem = async () => {
    if (!contractResult) return;

    try {
      const result = await showPopup({
        title: t('confirmSellTitle'),
        message: t('confirmSellMessage', {
          item: extractSkinName(contractResult.outputItem.name || ''),
          price: formatCreditsNumber(contractResult.outputItem.value || 0),
        }),
        buttons: [
          { id: 'cancel', type: 'cancel' },
          {
            id: 'confirm',
            type: 'default',
            text: t('confirmSellConfirm'),
          },
        ],
      });

      if (result === 'confirm') {
        setIsExecuting(true);
        try {
          await apiClient.api.inventoryControllerSellInventoryItem(contractResult.outputItem.id);
          playEarnings();
          queryClient.invalidateQueries({ queryKey: queryKeys.profile });
          queryClient.invalidateQueries({ queryKey: ['inventory'] });
          notification('success');
          resetContract();
        } catch (err) {
          notification('error');
          setError({ kind: 'sell', text: apiErrorMessage(err) ?? t('sellError') });
        } finally {
          setIsExecuting(false);
        }
      }
    } catch (err) {
      console.error('Popup error:', err);
    }
  };

  useEffect(() => {
    const handleFocus = () => {
      if (!showResult) queryClient.invalidateQueries({ queryKey: ['inventory'] });
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [showResult, queryClient]);

  const fetchPreview = useCallback(async () => {
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    setPreviewLoading(true);

    try {
      const itemIds = selectedItems.map((item) => item.id);
      const response = await apiClient.api.contractsControllerPreviewContract({
        itemIds,
      });
      if (requestId !== previewRequestRef.current) return;
      setPreview(response);
      setError(null);
    } catch (err) {
      if (requestId !== previewRequestRef.current) return;
      console.error('Failed to fetch preview:', formatApiError(err));
      setPreview(null);
      setError({ kind: 'preview', text: apiErrorMessage(err) ?? t('errorsPreviewFailed') });
    } finally {
      if (requestId === previewRequestRef.current) setPreviewLoading(false);
    }
  }, [apiClient, selectedItems, t]);

  useEffect(() => {
    const totalSelectedValue = selectedItems.reduce((sum, item) => sum + item.acquiredPrice, 0);

    if (selectedItems.length < CONTRACT_LIMITS.MIN_ITEMS || totalSelectedValue < CONTRACT_LIMITS.MIN_TOTAL_VALUE) {
      previewRequestRef.current += 1;
      setPreviewLoading(false);
      setPreview(null);
      setError(null);
      return;
    }

    setPreviewLoading(true);
    const timer = setTimeout(() => void fetchPreview(), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [selectedItems, fetchPreview]);

  const handleItemSelect = useCallback((item: UserInventoryItemResponse) => {
    setSelectedItems((prev) => {
      if (prev.length >= CONTRACT_LIMITS.MAX_ITEMS) return prev;
      if (prev.some((s) => s.id === item.id)) return prev;
      const contractItem: ContractItem = {
        id: item.id,
        itemHashName: item.item.hashName,
        acquiredPrice: item.acquiredPriceInCredits,
        item: {
          name: item.item.name,
          imageUrl: item.item.imageUrl || '',
          rarityColor: item.item.rarityColor || '',
          rarityName: item.item.rarityName || '',
          wear: item.item.wear,
        },
      };
      return [...prev, contractItem];
    });
  }, []);

  const handleItemDeselect = useCallback((itemId: string) => {
    setSelectedItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  const handleRemoveAll = useCallback(() => {
    impact('light');
    setSelectedItems([]);
  }, [impact]);

  const handleExecuteContract = async () => {
    const validation = validateContractSelection(selectedItems);
    if (!validation.valid) {
      return;
    }

    const result = await showPopup({
      title: t('confirmContractTitle'),
      message: t('confirmContractMessage', {
        count: selectedItems.length,
        value: formatCreditsNumber(totalValue),
      }),
      buttons: [
        { id: 'cancel', type: 'cancel' },
        {
          id: 'confirm',
          type: 'default',
          text: t('confirmContractConfirm'),
        },
      ],
    });

    if (result !== 'confirm') return;

    impact('heavy');

    try {
      setIsExecuting(true);
      setError(null);

      const totalInputValue = selectedItems.reduce((sum, item) => sum + item.acquiredPrice, 0);

      const itemIds = selectedItems.map((item) => item.id);
      const response = await apiClient.api.contractsControllerExecuteContract({
        inputItemIds: itemIds,
      });

      setContractResult({ ...response, totalInputValue });

      await roll.play({
        value: response.outputItem.value,
        preview,
        isSuccessful: response.outputItem.value > totalInputValue,
      });

      setShowResult(true);
    } catch (err) {
      notification('error');
      setError({ kind: 'execute', text: apiErrorMessage(err) ?? t('errorsExecutionFailed') });
    } finally {
      resetRoll();
      setIsExecuting(false);
    }
  };

  const handleItemToggle = useCallback(
    (item: UserInventoryItemResponse) => {
      if (selectedItems.some((s) => s.id === item.id)) {
        handleItemDeselect(item.id);
        impact('light');
      } else if (selectedItems.length < CONTRACT_LIMITS.MAX_ITEMS) {
        handleItemSelect(item);
        impact('light');
      }
    },
    [selectedItems, handleItemSelect, handleItemDeselect, impact],
  );

  const openSheet = useCallback(() => setSheetOpen(true), []);
  const closeSheet = useCallback(() => setSheetOpen(false), []);

  const selectedIds = useMemo(() => new Set(selectedItems.map((s) => s.id)), [selectedItems]);

  const validation = validateContractSelection(selectedItems);
  const canExecute = validation.valid && !isExecuting && !roll.animating && !previewLoading && preview !== null;
  const totalValue = selectedItems.reduce((sum, item) => sum + item.acquiredPrice, 0);

  const requirementText =
    validation.errorCode === 'min_items'
      ? t('errorsMinItemsRequired', {
          min: CONTRACT_LIMITS.MIN_ITEMS,
          items: pluralize(
            CONTRACT_LIMITS.MIN_ITEMS,
            { one: t('itemsOne'), few: t('itemsFew'), many: t('itemsMany') },
            lang,
          ),
        })
      : validation.errorCode === 'min_value'
        ? t('errorsMinValueRequired', { min: CONTRACT_LIMITS.MIN_TOTAL_VALUE })
        : null;
  const crateResultItem = contractResult?.outputItem;
  const scaleLo = roll.scale?.lo ?? preview?.minValue ?? 0;
  const scaleHi = roll.scale?.hi ?? preview?.maxValue ?? 0;
  const inputT =
    scaleHi > scaleLo ? Math.min(Math.max((totalValue - scaleLo) / (scaleHi - scaleLo), 0), 1) : 0.5;
  const needleLeft = roll.animating ? roll.needleT : inputT;
  const needlePrice = roll.animating ? Math.round(scaleLo + roll.needleT * (scaleHi - scaleLo)) : totalValue;

  const headerButtonClass = 'group h-11 w-11 bg-transparent p-0 text-white/60 hover:text-white/90';
  const headerButtonInnerClass =
    'grid h-8 w-8 place-items-center rounded-full border border-white/[0.1] bg-white/[0.06] transition-colors group-hover:bg-white/[0.12]';

  const resultActions =
    !roll.animating && showResult ? (
      <>
        {error && (
          <div className="rounded-xl bg-accent-red/10 p-3 text-center">
            <Text color="error" className="text-sm">{error.text}</Text>
          </div>
        )}
        <Button
          size="l"
          stretched
          loading={isShareContractLoading}
          before={<FaShare size={16} />}
          onClick={handleShareContract}
          variant={isContractBigWin ? 'hero' : 'share'}
        >
          {isContractBigWin ? t('shareBigWin') : t('share')}
        </Button>
        {(contractResult?.outputItem.value ?? 0) >= MIN_UPGRADE_ITEM_PRICE ? (
          <div className="flex gap-2">
            <Button size="l" stretched variant="secondary" onClick={handleSellItem} disabled={isExecuting}>
              {t('sell')} <Credits amount={contractResult?.outputItem.value ?? 0} />
            </Button>
            <Button size="l" stretched variant="primary" onClick={handleUpgradeResult} disabled={isExecuting}>
              {t('upgrade')}
            </Button>
          </div>
        ) : (
          <Button size="l" stretched variant="secondary" onClick={handleSellItem} disabled={isExecuting}>
            {t('sell')} <Credits amount={contractResult?.outputItem.value ?? 0} />
          </Button>
        )}
        <Button size="l" stretched variant="cta" onClick={resetContract}>
          {t('resultExecuteAnother')}
        </Button>
      </>
    ) : null;

  const crateNode = (
    <>
      <Crate
        items={selectedItems}
        phase={roll.phase}
        resultItem={crateResultItem}
        locked={roll.animating}
        onRemoveItem={handleItemDeselect}
      />

      <div className="flex min-h-9 items-center justify-center">
        {selectedItems.length > 0 && !roll.animating && (
          <Button
            size="s"
            variant="tertiary"
            data-testid="contract-remove-all"
            data-sound="menu_select"
            onClick={handleRemoveAll}
          >
            {t('removeAll')}
          </Button>
        )}
      </div>
    </>
  );

  const previewErrorNode = error?.kind === 'preview' && (
    <div className="w-full max-w-[420px] px-4">
      <div className="rounded-xl bg-accent-red/10 p-3 flex flex-col items-center gap-2 text-center">
        <Text color="error" className="text-sm">{error.text}</Text>
        <Button size="m" variant="secondary" onClick={fetchPreview}>
          {t('previewRetry')}
        </Button>
      </div>
    </div>
  );

  const rangeBarNode = (
    <PrizeRangeBar
      lo={scaleLo}
      hi={scaleHi}
      price={needlePrice}
      needleLeft={needleLeft}
      animating={roll.animating}
      loading={previewLoading}
      labels={{
        min: t('statsMinPrize'),
        yourItems: t('statsYourItems'),
        max: t('statsMaxPrize'),
      }}
    />
  );

  const executeNode = (
    <div className="w-full max-w-[420px] px-4">
      {requirementText ? (
        <Text color="secondary" className="block text-center text-sm">
          {requirementText}
        </Text>
      ) : (
        <Button
          size="l"
          stretched
          variant="hero"
          disabled={!canExecute}
          onClick={handleExecuteContract}
          data-sound="cta"
        >
          {t('executeContract')}
        </Button>
      )}
    </div>
  );

  return (
    <>
      {!showResult && (
        <ScreenFixedTop title={t('title')}>
          <div className={cn('flex items-center justify-end gap-1 pe-2', isDesktop ? 'py-1' : '-mt-1 pb-1')}>
            <Button
              variant="tertiary"
              data-sound="menu_select"
              data-testid="contract-history-button"
              aria-label={t('history')}
              className={headerButtonClass}
              onClick={() => setHistoryOpen(true)}
            >
              <span className={headerButtonInnerClass}>
                <FaClock size={15} />
              </span>
            </Button>
            <Button
              variant="tertiary"
              data-sound="menu_select"
              data-testid="contract-faq-button"
              aria-label={t('faqHowItWorks')}
              className={headerButtonClass}
              onClick={() => setFaqOpen(true)}
            >
              <span className={headerButtonInnerClass}>
                <FaQuestionCircle size={16} />
              </span>
            </Button>
          </div>
        </ScreenFixedTop>
      )}
      {!(isDesktop && showResult) && (
        <ScreenFixedBottom>
          {resultActions ? <ButtonGroup>{resultActions}</ButtonGroup> : null}
        </ScreenFixedBottom>
      )}
      <ScreenContent className="flex flex-col gap-2.5" scrollRef={pageScrollRef}>
        {showResult && contractResult ? (
          isDesktop ? (
            <DesktopResultLayout
              card={<ContractSuccessResult outputItem={contractResult.outputItem} />}
              actions={<ButtonGroup>{resultActions}</ButtonGroup>}
            />
          ) : (
            <div className="w-full pb-[var(--screen-fixed-bottom-height,0px)] [animation:fadeIn_0.5s_ease-out_forwards]">
              <ContractSuccessResult outputItem={contractResult.outputItem} />
            </div>
          )
        ) : isDesktop ? (
          <div className="mx-auto grid w-full max-w-[1200px] grid-cols-[minmax(0,1fr)_minmax(0,440px)] items-start gap-6 p-6">
            <div className="flex flex-col items-center gap-3">
              {crateNode}
              {previewErrorNode}
              {rangeBarNode}
              {executeNode}
            </div>
            <section className="min-w-0 rounded-2xl border border-white/[0.06] bg-white/[0.03]">
              <h2 className="m-0 px-4 pt-4 pb-2 text-sm font-bold text-white">
                {t('selectItemsSheetTitle')} ({selectedItems.length}/{CONTRACT_LIMITS.MAX_ITEMS})
              </h2>
              <div className="px-3 pb-3">
                <ContractInventoryList
                  selectedIds={selectedIds}
                  maxItems={CONTRACT_LIMITS.MAX_ITEMS}
                  onToggle={handleItemToggle}
                  scrollRef={pageScrollRef}
                />
              </div>
            </section>
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center gap-3 flex-1 w-full">
              {crateNode}
              {previewErrorNode}
              {rangeBarNode}
              {executeNode}
            </div>

            {error && error.kind !== 'preview' && (
              <div className="px-4 w-full max-w-[420px] mx-auto">
                <div className="rounded-xl bg-accent-red/10 p-3 text-center">
                  <Text color="error" className="text-sm">{error.text}</Text>
                </div>
              </div>
            )}

            <div className="px-4 w-full max-w-[420px] mx-auto">
              <Button
                size="l"
                stretched
                variant={validation.valid ? 'secondary' : 'cta'}
                before={<FaPlus size={16} />}
                disabled={roll.animating}
                onClick={openSheet}
              >
                {t('selectItems')} ({selectedItems.length}/{CONTRACT_LIMITS.MAX_ITEMS})
              </Button>
            </div>
          </>
        )}
      </ScreenContent>
      {showResult && contractResult && contractResult.outputItem.value > contractResult.totalInputValue && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <Confetti
            mode="fall"
            particleCount={30}
            colors={['#ff577f', '#ff884b', '#ffd384', '#fff9b0', '#3498db']}
            shapeSize={10}
          />
        </div>
      )}
      <ContractInventorySheet
        isOpen={sheetOpen}
        onClose={closeSheet}
        selectedIds={selectedIds}
        maxItems={CONTRACT_LIMITS.MAX_ITEMS}
        onToggle={handleItemToggle}
      />
      <ContractHistorySheet isOpen={historyOpen} onClose={() => setHistoryOpen(false)} />
      <ContractFAQSheet isOpen={faqOpen} onClose={() => setFaqOpen(false)} />
    </>
  );
};
